import { lazy, Suspense } from 'react';
import { useModal } from '../../contexts/ModalContext';
import { BaseModalLoader } from './BaseModalLoader';
import EmploymentScheduleModal from './EmploymentScheduleModal';
import AddEmploymentScheduleModal from './AddEmploymentScheduleModal';
import ProjectContactModal from './contact/ProjectContactModal';
import AbsencesModal from './AbsencesModal';
import InvoiceModal from './InvoiceModal';
import ExtraBillingModal from '../../pages/projects/projectPage/ExtraBillingModal';

const CreateAbsenceModal = lazy(() => import('../modals/CreateAbsenceModal'));
const TimeReportModal = lazy(() => import('../modals/TimeReportModal'));
const InvoiceExportDateModal = lazy(() => import('../modals/InvoiceExportDateModal'));
const FortnoxExportRejectedModal = lazy(() => import('../modals/FortnoxExportRejectedModal'));

const VehiclesModal = lazy(() => import('../../pages/resources/vehiclesModal/VehiclesModal'));
const ContactModal = lazy(() => import('./contact/ContactModal'));
const EmployeeSetupModal = lazy(() => import('./EmployeeSetupModal'));
const OrganizationSetUpModal = lazy(() => import('./OrganizationSetUpModal'));
const CustomerModal = lazy(() => import('../../pages/customers/CustomerModal'));
const CaseModal = lazy(() => import('../../pages/case/CaseModal'));
const QualityControlModal = lazy(() => import('../../pages/qualityControl/QualityControlModal'));
const EmployeeModal = lazy(() => import('../../pages/resources/employeeModal/EmployeeModal'));
const OrderModalEdit = lazy(() => import('../../pages/order/orderModal/OrderModalEdit'));
const OrderModalCreate = lazy(() => import('../../pages/order/orderModal/OrderModalCreate'));
const ContractProductModal = lazy(
  () => import('../../pages/contracts/contractPage/modals/ContractProductModal')
);
const WorkTaskTemplateModal = lazy(
  () => import('../../pages/templates/modals/WorkTaskTemplateModal')
);
const WorkTaskJobModal = lazy(() => import('../../pages/templates/modals/WorkTaskJobModal'));
const WorkTaskAreaModal = lazy(() => import('../../pages/templates/modals/WorkTaskAreaModal'));
const PriceAdjustmentModal = lazy(
  () => import('../../pages/templates/priceAdjustment/PriceAdjustmentModal')
);
const InvoiceTemplateModal = lazy(
  () => import('../../pages/templates/modals/InvoiceTemplateModal')
);
const IntervalModal = lazy(() => import('../../pages/intervals/IntervalModal'));
const SupportModal = lazy(() => import('./support/SupportModal'));
const VehicleDeviationModal = lazy(
  () => import('../../pages/resources/vehiclesModal/VehiclesDeviationModal')
);
const SignConfirmationModal = lazy(() => import('../../pages/publicAccess/SignConfirmationModal'));
const ConfirmModal = lazy(() => import('../modals/ConfirmModal'));
const ProjectModal = lazy(() => import('../../pages/projects/projectModal/ProjectModal'));
const ContractModal = lazy(() => import('../../pages/contracts/contractModal/ContractModal'));
const ArchiveFilePickerModal = lazy(() => import('../attachments/ArchiveFilePickerModal'));
const InvoiceTemplatePickerModal = lazy(
  () => import('../../pages/templates/modals/InvoiceTemplatePickerModal')
);

export const AuthenticatedGlobalModals = () => {
  const { modalState } = useModal();

  return (
    <Suspense fallback={<BaseModalLoader />}>
      {modalState?.OrderModalEdit?.open && <OrderModalEdit name="OrderModalEdit" />}
      {modalState?.OrderModalCreate?.open && <OrderModalCreate name="OrderModalCreate" />}
      {modalState?.ContractModal?.open && (
        <ContractModal name="ContractModal" title="Skapa en ny offert" />
      )}
      {modalState?.SignConfirmationModal?.open && (
        <SignConfirmationModal name="SignConfirmationModal" />
      )}
      {modalState?.ConfirmModal?.open && <ConfirmModal name="ConfirmModal" />}
      {modalState?.ProjectModal?.open && (
        <ProjectModal name="ProjectModal" title="Skapa en nytt projekt" />
      )}
      {modalState?.TimeReportModal?.open && <TimeReportModal name="TimeReportModal" />}
      {modalState?.EmployeeModal?.open && <EmployeeModal name="EmployeeModal" />}
      {modalState?.CreateAbsenceModal?.open && <CreateAbsenceModal name="CreateAbsenceModal" />}
      {modalState?.AddEmploymentScheduleModal?.open && (
        <AddEmploymentScheduleModal name="AddEmploymentScheduleModal" />
      )}
      {modalState?.EmploymentScheduleModal?.open && (
        <EmploymentScheduleModal name="EmploymentScheduleModal" />
      )}
      {modalState?.ContractModal?.open && <ContractModal name="ContractModal" />}
      {modalState?.OrgSetup?.open && <OrganizationSetUpModal name="OrgSetup" />}
      {modalState?.EmployeeSetup?.open && <EmployeeSetupModal name="EmployeeSetup" />}
      {modalState?.ContactModal?.open && (
        <ContactModal name="ContactModal" title="Skapa en ny mottagare" />
      )}
      {modalState?.ProjectContactModal?.open && <ProjectContactModal name="ProjectContactModal" />}
      {modalState?.VehiclesModal?.open && <VehiclesModal name="VehiclesModal" />}
      {modalState?.CustomerModal?.open && <CustomerModal name="CustomerModal" />}
      {modalState?.CaseModal?.open && <CaseModal name="CaseModal" />}
      {modalState?.QualityControlModal?.open && <QualityControlModal name="QualityControlModal" />}
      {modalState?.ContractProductModal?.open && (
        <ContractProductModal name="ContractProductModal" />
      )}
      {modalState?.WorkTaskTemplateModal?.open && (
        <WorkTaskTemplateModal name="WorkTaskTemplateModal" />
      )}
      {modalState?.AbsencesModal?.open && <AbsencesModal name="AbsencesModal" />}
      {modalState?.InvoiceModal?.open && <InvoiceModal name="InvoiceModal" />}
      {modalState?.WorkTaskJobModal?.open && <WorkTaskJobModal name="WorkTaskJobModal" />}
      {modalState?.WorkTaskAreaModal?.open && <WorkTaskAreaModal name="WorkTaskAreaModal" />}
      {modalState?.PriceAdjustmentModal?.open && (
        <PriceAdjustmentModal name="PriceAdjustmentModal" />
      )}
      {modalState?.InvoiceTemplateModal?.open && (
        <InvoiceTemplateModal name="InvoiceTemplateModal" />
      )}
      {modalState?.IntervalModal?.open && <IntervalModal name="IntervalModal" />}
      {modalState?.SupportModal?.open && <SupportModal name="SupportModal" />}
      {modalState?.VehicleDeviationModal?.open && (
        <VehicleDeviationModal name="VehicleDeviationModal" />
      )}
      {modalState?.ExtraBillingModal?.open && <ExtraBillingModal name="ExtraBillingModal" />}
      {modalState?.ArchiveFilePickerModal?.open && (
        <ArchiveFilePickerModal name="ArchiveFilePickerModal" />
      )}
      {modalState?.InvoiceTemplatePickerModal?.open && (
        <InvoiceTemplatePickerModal name="InvoiceTemplatePickerModal" />
      )}
      {modalState?.InvoiceExportDateModal?.open && (
        <InvoiceExportDateModal name="InvoiceExportDateModal" />
      )}
      {modalState?.FortnoxExportRejectedModal?.open && (
        <FortnoxExportRejectedModal name="FortnoxExportRejectedModal" />
      )}
    </Suspense>
  );
};
